/**
 * AuthContext — contexto de autenticação OIDC para a SPA Nava Flow.
 *
 * Responsabilidades:
 *   1. Manter o estado de sessão (user claims, loading, error).
 *   2. Expor login(email, password), loginSso(), logout(), submitMfa(code).
 *   3. Gerenciar o ciclo de vida do access token:
 *      - Troca de código OIDC (callback /auth/callback).
 *      - Refresh silencioso 2 minutos antes do vencimento.
 *   4. Persistir refresh_token em sessionStorage (sobrevive a F5, não a fechar aba).
 *
 * Padrões:
 *   - OWASP: access_token em memória, refresh em sessionStorage, nunca em URL.
 *   - SDLC-Seguro: nenhum dado sensível em console.log.
 *   - Clean: AuthProvider tem uma única responsabilidade; não conhece rota/UI.
 *
 * Para trocar o stub por IdP real (Keycloak/Cognito):
 *   - Substitua os imports de `./oidcStub` por `./oidcClient`.
 *   - A interface de login/logout/refresh permanece idêntica.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useRef,
} from 'react';
import {
  authenticateCredentials,
  exchangeCode,
  refreshTokens as refreshStubTokens,
  verifyMfaCode,
} from './oidcStub';
import { isSsoToken, redirectToSso, refreshSsoTokens } from './ssoClient';
import {
  setAccessToken,
  getAccessToken,
  clearAllTokens,
  setRefreshToken,
  getRefreshToken,
  setIdToken,
  decodeJwtPayload,
  isTokenExpired,
} from './tokenStorage';

// ─── Refresh de tokens ───────────────────────────────────────────────────────

/**
 * Renova a sessão pela via correta conforme a origem do login.
 *
 * Sessões SSO renovam no backend (o usuário do Entra não existe na lista de
 * demo do stub); sessões de credenciais seguem no stub. A escolha é feita pelo
 * claim `amr` do próprio token, então nada precisa ser persistido.
 *
 * @param {string} refreshToken
 * @returns {Promise<object>} TokenResponse
 */
async function refreshTokens(refreshToken) {
  return isSsoToken(refreshToken)
    ? refreshSsoTokens(refreshToken)
    : refreshStubTokens(refreshToken);
}

// ─── Tipos / shapes ──────────────────────────────────────────────────────────

/**
 * @typedef {object} AuthUser
 * @property {string} sub
 * @property {string} name
 * @property {string} email
 * @property {string} role
 * @property {string} matrix_id    — 'm-admin' | 'm-manager' | 'm-analyze' | 'm-estimative' | 'm-viewer'
 * @property {string} tenant_id
 */

/**
 * @typedef {'idle'|'loading'|'authenticated'|'mfa-required'|'unauthenticated'|'error'} AuthStatus
 */

// ─── Estado inicial ──────────────────────────────────────────────────────────

/** @returns {object} estado inicial do reducer */
function initialState() {
  return {
    status:      'idle',    // AuthStatus
    user:        null,      // AuthUser | null
    error:       null,      // string | null
    mfaPending:  false,     // aguardando código MFA
    // Dados temporários necessários entre etapas de MFA
    _pendingTokens: null,
  };
}

// ─── Reducer ─────────────────────────────────────────────────────────────────

function authReducer(state, action) {
  switch (action.type) {
    case 'LOADING':
      return { ...state, status: 'loading', error: null };

    case 'AUTHENTICATED':
      return {
        ...initialState(),
        status: 'authenticated',
        user:   action.payload.user,
      };

    case 'MFA_REQUIRED':
      return {
        ...state,
        status:         'mfa-required',
        mfaPending:     true,
        _pendingTokens: action.payload.tokens,
        error:          null,
      };

    case 'MFA_ERROR':
      return {
        ...state,
        error: action.payload.message,
      };

    case 'ERROR':
      return {
        ...state,
        status: 'error',
        error:  action.payload.message,
        user:   null,
      };

    case 'LOGOUT':
      // 'unauthenticated' (não 'idle') para o AuthGuard redirecionar ao /login.
      // 'idle' é reservado ao boot antes de restoreSession() resolver.
      return { ...initialState(), status: 'unauthenticated' };

    default:
      return state;
  }
}

// ─── Context ─────────────────────────────────────────────────────────────────

const AuthContext = createContext(null);

// ─── Provider ────────────────────────────────────────────────────────────────

/**
 * AuthProvider — deve envolver toda a árvore da aplicação (acima do Router).
 * Tenta restaurar a sessão do refresh token ao montar.
 */
export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(authReducer, undefined, initialState);
  const refreshTimerRef   = useRef(null);

  // ── Restaurar sessão ao montar ─────────────────────────────────────────────
  useEffect(() => {
    restoreSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Agenda refresh silencioso ──────────────────────────────────────────────
  const scheduleRefresh = useCallback((accessToken) => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);

    const payload = decodeJwtPayload(accessToken);
    if (!payload || !payload.exp) return;

    const now         = Math.floor(Date.now() / 1000);
    const expiresIn   = payload.exp - now;
    // Refresh 2 minutos antes de vencer
    const delayMs     = Math.max((expiresIn - 120) * 1000, 0);

    refreshTimerRef.current = setTimeout(async () => {
      const rt = getRefreshToken();
      if (!rt) return;
      try {
        const tokens = await refreshTokens(rt);
        applyTokens(tokens);
      } catch {
        // Refresh falhou → logout silencioso
        logout();
      }
    }, delayMs);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Aplica tokens na memória e agenda refresh ──────────────────────────────
  const applyTokens = useCallback((tokens, userOverride = null) => {
    setAccessToken(tokens.access_token);
    if (tokens.refresh_token) setRefreshToken(tokens.refresh_token);
    if (tokens.id_token)      setIdToken(tokens.id_token);

    const claims = decodeJwtPayload(tokens.access_token);
    const user   = userOverride ?? {
      sub:       claims?.sub       ?? '',
      name:      claims?.name      ?? '',
      email:     claims?.email     ?? '',
      role:      claims?.role      ?? '',
      matrix_id: claims?.matrix_id ?? '',
      tenant_id: claims?.tenant_id ?? '',
    };

    dispatch({ type: 'AUTHENTICATED', payload: { user } });
    scheduleRefresh(tokens.access_token);
  }, [scheduleRefresh]);

  // ── Restaurar sessão com refresh token existente ───────────────────────────
  async function restoreSession() {
    const rt = getRefreshToken();
    if (!rt) {
      // Sem sessão anterior → sai de 'idle' para 'unauthenticated', senão o
      // AuthGuard ficaria preso no spinner e nunca redirecionaria ao /login.
      dispatch({ type: 'LOGOUT' });
      return;
    }

    dispatch({ type: 'LOADING' });
    try {
      const tokens = await refreshTokens(rt);
      applyTokens(tokens);
    } catch {
      // Refresh inválido → sessão expirada, iniciar do zero
      clearAllTokens();
      dispatch({ type: 'LOGOUT' });
    }
  }

  // ── login com email/senha ──────────────────────────────────────────────────
  const login = useCallback(async (email, password) => {
    dispatch({ type: 'LOADING' });
    try {
      const { user, authCode } = await authenticateCredentials(email, password);
      const tokens = await exchangeCode(authCode, '');

      if (tokens.requiresMfa) {
        // Guarda tokens pendentes até MFA ser completado
        dispatch({ type: 'MFA_REQUIRED', payload: { tokens } });
        return;
      }

      applyTokens(tokens, {
        sub:       user.sub,
        name:      user.name,
        email:     user.email,
        role:      user.role,
        matrix_id: user.matrix_id,
        tenant_id: user.tenant_id,
      });
    } catch (err) {
      const message =
        err.message === 'INVALID_CREDENTIALS'
          ? 'E-mail ou senha inválidos. Verifique suas credenciais e tente novamente.'
          : 'Erro ao autenticar. Tente novamente em instantes.';
      dispatch({ type: 'ERROR', payload: { message } });
    }
  }, [applyTokens]);

  // ── login SSO (Microsoft Entra ID, redirect real) ─────────────────────────
  /**
   * Sai da SPA e entrega o fluxo ao backend (/auth/sso/start), que monta o
   * state/PKCE e redireciona para a Microsoft. O client secret e a troca do
   * authorization code ficam no servidor — a SPA nunca os vê.
   *
   * @param {string} nextPath — rota para onde voltar depois de autenticar.
   */
  const loginSso = useCallback((nextPath = '/dashboard') => {
    dispatch({ type: 'LOADING' });
    // Navegação de página inteira: daqui em diante quem conduz é o browser.
    redirectToSso(nextPath);
  }, []);

  // ── aplica os tokens devolvidos pelo callback do SSO ──────────────────────
  /**
   * Recebe o par access/refresh emitido pelo backend após o SSO e ativa a
   * sessão — mesmo caminho do login por credenciais (memória + refresh
   * agendado), de modo que o resto do app não distingue as duas origens.
   *
   * @param {{ access_token: string, refresh_token: string }} tokens
   */
  const applySsoTokens = useCallback((tokens) => {
    applyTokens(tokens);
  }, [applyTokens]);

  // ── submitMfa — valida o código MFA ───────────────────────────────────────
  const submitMfa = useCallback(async (code) => {
    const pendingTokens = state._pendingTokens;
    if (!pendingTokens) return;

    dispatch({ type: 'LOADING' });
    try {
      const valid = await verifyMfaCode(code);
      if (!valid) {
        dispatch({
          type: 'MFA_ERROR',
          payload: { message: 'Código inválido. Digite os 6 dígitos do seu autenticador.' },
        });
        return;
      }
      applyTokens(pendingTokens);
    } catch {
      dispatch({
        type: 'MFA_ERROR',
        payload: { message: 'Erro ao verificar MFA. Tente novamente.' },
      });
    }
  }, [state._pendingTokens, applyTokens]);

  // ── logout ─────────────────────────────────────────────────────────────────
  const logout = useCallback(() => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    clearAllTokens();
    dispatch({ type: 'LOGOUT' });
  }, []);

  // Limpa timer ao desmontar
  useEffect(() => {
    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, []);

  const value = {
    // Estado
    status:     state.status,
    user:       state.user,
    error:      state.error,
    mfaPending: state.mfaPending,
    isAuthenticated: state.status === 'authenticated',
    isLoading:       state.status === 'loading',

    // Ações
    login,
    loginSso,
    applySsoTokens,
    submitMfa,
    logout,

    // Acesso ao token atual (para o api/client)
    getToken: getAccessToken,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// ─── Hooks ───────────────────────────────────────────────────────────────────

/**
 * useAuth — hook principal para consumir o contexto de autenticação.
 *
 * @returns {{ status, user, error, mfaPending, isAuthenticated, isLoading, login, loginSso, applySsoTokens, submitMfa, logout, getToken }}
 */
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth deve ser usado dentro de <AuthProvider>');
  }
  return ctx;
}
